import type { LoaderFunctionArgs } from 'react-router-dom';
import { redirect } from 'react-router-dom';
import useAuthStore from '~/authentication/store';

import availableLanguages from '../../../resources/languages.json';

type HostGuardOptions = {
  allowOnboarding?: boolean;
  allowAdmin?: boolean;
};

const SUPPORTED_LOCALES = new Set(
  (availableLanguages as string[]).map((lang) => lang.toLowerCase()),
);
const DEFAULT_LOCALE = 'en';
const LOCALE_REGEX = /^\/([a-z]{2})(?=\/|$)/i;

const normalizeLocale = (candidate?: string | null) => {
  if (!candidate) return DEFAULT_LOCALE;
  const lower = candidate.toLowerCase();
  return SUPPORTED_LOCALES.has(lower) ? lower : DEFAULT_LOCALE;
};

const detectRequestedLocale = (pathname: string) => {
  const match = pathname.match(LOCALE_REGEX);
  return normalizeLocale(match?.[1]);
};

const buildLocalizedPath = (pathname: string, locale: string) => {
  const normalized = normalizeLocale(locale);
  const base = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (normalized === DEFAULT_LOCALE) {
    return base;
  }
  return `/${normalized}${base}`;
};

const buildLoginRedirect = (url: URL, locale: string) => {
  const loginPath = buildLocalizedPath('/signup/', locale);
  const loginUrl = new URL(loginPath, url.origin);
  loginUrl.searchParams.set(
    'returnTo',
    `${url.pathname}${url.search}${url.hash}`,
  );
  loginUrl.searchParams.set('modal', '1');
  return `${loginUrl.pathname}${loginUrl.search}${loginUrl.hash}`;
};

const buildHomeRedirect = (url: URL, locale: string) => {
  const homePath = buildLocalizedPath('/', locale);
  const homeUrl = new URL(homePath, url.origin);
  homeUrl.search = url.search;
  homeUrl.hash = url.hash;
  return `${homeUrl.pathname}${homeUrl.search}${homeUrl.hash}`;
};

const ensureAuthReady = (signal: AbortSignal, timeoutMs = 5000) =>
  new Promise<void>((resolve) => {
    try {
      const state = useAuthStore.getState();
      if (state?.user) {
        resolve();
        return;
      }
      let seenLoading = state?.loading === true;

      if (state?._hasHydrated && !state.loading && seenLoading) {
        resolve();
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      let timer: ReturnType<typeof setTimeout> | null = null;
      let unsub: () => void = () => {};

      const onAbort = () => {
        try {
          unsub();
        } catch {}
        if (timer) clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
        resolve();
      };

      signal.addEventListener('abort', onAbort);

      unsub = useAuthStore.subscribe((s) => {
        if (!seenLoading && s.loading) {
          seenLoading = true;
        }
        if (s.user || (seenLoading && !s.loading)) {
          try {
            unsub();
          } catch {}
          if (timer) clearTimeout(timer);
          signal.removeEventListener('abort', onAbort);
          finish();
        }
      });

      timer = setTimeout(() => {
        try {
          unsub();
        } catch {}
        signal.removeEventListener('abort', onAbort);
        finish();
      }, timeoutMs);
    } catch {
      resolve();
    }
  });

type AccessSnapshot = {
  isLoggedIn: boolean;
  isHost: boolean;
  isOnboardingHost: boolean;
  isAdmin: boolean;
  canAccessHostArea: boolean;
};

const getAccessSnapshot = (
  auth: ReturnType<typeof useAuthStore.getState>,
  allowOnboarding: boolean,
  allowAdmin: boolean,
): AccessSnapshot => {
  const isLoggedIn = auth.isLoggedIn?.() ?? false;
  const isHost = auth.isHost?.() ?? false;
  const isOnboardingHost =
    allowOnboarding &&
    ((auth.isOnboardingHost?.() ?? false) ||
      (auth.isOnboardingHostWebinar?.() ?? false));
  const isAdmin = allowAdmin && (auth.isAdmin?.() ?? false);
  const canAccessHostArea = isHost || isOnboardingHost || isAdmin;
  return { isLoggedIn, isHost, isOnboardingHost, isAdmin, canAccessHostArea };
};

const decideRedirect = (
  snapshot: AccessSnapshot,
  url: URL,
  locale: string,
): string | null => {
  if (!snapshot.isLoggedIn) return buildLoginRedirect(url, locale);
  if (!snapshot.canAccessHostArea) return buildHomeRedirect(url, locale);
  return null;
};

export const createHostGuardLoader =
  ({ allowOnboarding = true, allowAdmin = true }: HostGuardOptions = {}) =>
  async ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const locale = detectRequestedLocale(url.pathname);

    await ensureAuthReady(request.signal);
    if (request.signal.aborted) return null;

    const auth = useAuthStore.getState();
    if (!auth?._hasHydrated || auth.loading) {
      return null;
    }
    const snapshot = getAccessSnapshot(auth, allowOnboarding, allowAdmin);

    const to = decideRedirect(snapshot, url, locale);
    return to ? redirect(to) : null;
  };
